import { useState, type ReactNode } from 'react';
import {
  BusOfferShortlist,
  toBusFilterableOffers,
  type BusFilterableOffer,
  type BusLegCities,
  type BusSeatLayout,
} from '@voll-intelligence/intelligence-ui-kit';
import { toDateOnly } from '@voll-intelligence/dates';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import type { BusOfferSegmentSelection, ChatUiBusOffer } from '@voll-intelligence/genui-contract';
import {
  busLegSteps,
  busShortlistCopy,
  busViewCopy,
  withHighlights,
  type BusOutboundPickView,
} from '../../shared/bus_results_copy.js';
import {
  BusOfferSeats,
  TRAVELER_ID,
  type BusSeatPriceScope,
  type BusSeatTraveler,
} from './bus_offer_seats.js';
import { BusResultsDialog } from './bus_results_dialog.js';
import { BLOCK_ELEVATION } from '../../../shared/ui/block_elevation.js';

/** As cidades da perna: as do pedido quando o agente as manda; senão (blocos
 *  antigos) as rodoviárias da primeira viagem. */
function legCitiesOf(
  offers: readonly ChatUiBusOffer[],
  route: { from: string; to: string } | undefined,
): BusLegCities {
  if (route) return { departure: route.from, arrival: route.to };
  return {
    departure: offers[0]?.departure.stopName ?? '',
    arrival: offers[0]?.arrival.stopName ?? '',
  };
}

/** A seleção curta na ordem do agente (a recomendada primeiro); a viagem aberta
 *  que mora fora dela (uma escolha por texto da lista inteira) entra no fim. */
function shortlistOf(
  offers: readonly ChatUiBusOffer[],
  views: readonly BusFilterableOffer[],
  expanded: string | undefined,
) {
  const ids = offers.map((offer) => offer.offerId);
  if (expanded && !ids.includes(expanded)) ids.push(expanded);
  const viewOf = new Map(views.map((facts) => [facts.offer.id, facts.offer]));
  return ids.flatMap((id) => viewOf.get(id) ?? []);
}

/** Card congelado: as mesmas views, sem `Selecionar`. */
function frozen(views: BusFilterableOffer[]): BusFilterableOffer[] {
  return views.map((facts) => ({ ...facts, offer: { ...facts.offer, selectable: false } }));
}

/** A posição na lista COMPLETA (1 = primeira) — a ordem do cache do agente. */
type PickAt = (offer: ChatUiBusOffer, position: number) => void;

/** A viagem e a posição dela na lista completa, ou nada. */
function locate(all: readonly ChatUiBusOffer[], offerId: string, then: PickAt): void {
  const index = all.findIndex((offer) => offer.offerId === offerId);
  const offer = all[index];
  if (offer) then(offer, index + 1);
}

type ConfirmSeats = (offer: ChatUiBusOffer, segments: BusOfferSegmentSelection[]) => void;

/** O que a seleção curta e o diálogo recebem para abrir a escolha de poltrona
 *  no card: a viagem aberta, o miolo (`BusOfferSeats`) e o recolher. Confirmar
 *  fecha tudo e devolve a viagem com as poltronas e a posição na lista. */
function seatExpansionOf(input: {
  all: readonly ChatUiBusOffer[];
  cities: BusLegCities;
  travelers: readonly BusSeatTraveler[];
  scope: BusSeatPriceScope;
  expanded: string | undefined;
  /** Recolhe a escolha. */
  close: () => void;
  onConfirmSeats: ConfirmSeats;
}) {
  const { all, cities, travelers, scope, expanded, close, onConfirmSeats } = input;
  const renderExpansion = (offerId: string, layout: BusSeatLayout): ReactNode => {
    const offer = all.find((candidate) => candidate.offerId === offerId);
    if (!offer) return null;
    return (
      <BusOfferSeats
        offer={offer}
        cities={cities}
        travelers={travelers}
        scope={scope}
        layout={layout}
        onConfirm={(segments) => {
          close();
          locate(all, offerId, (picked) => {
            onConfirmSeats(picked, segments);
          });
        }}
        onCollapse={close}
      />
    );
  };
  return {
    renderExpansion,
    onCollapseExpansion: close,
    ...(expanded ? { expandedOfferId: expanded } : {}),
  };
}

/** A faixa de dias da perna em cena (frame 04): só no card vivo e com o dia
 *  buscado. Hoje é o piso; os limites vêm do agente. */
function dayStripOf(
  i18n: ReturnType<typeof useGenUiI18n>,
  strip: {
    day: string | undefined;
    dayBounds: { min?: string; max?: string } | undefined;
    onPickDay: ((day: string) => void) | undefined;
  },
) {
  const { day, dayBounds, onPickDay } = strip;
  if (!day || !onPickDay) return {};
  return {
    dayStrip: {
      day,
      today: toDateOnly(new Date()),
      ...(dayBounds ? { bounds: dayBounds } : {}),
      locale: i18n.locale,
      label: i18n.t('busResults.dayStrip.label'),
      onSelect: onPickDay,
    },
  };
}

/** A fala do agente sobre o card, em markdown quando o host o desenha. */
function CardNote({ note }: Readonly<{ note: string }>): ReactNode {
  const { renderMarkdown } = useGenUiConfig();
  return <div className="itin-intro">{renderMarkdown ? renderMarkdown(note) : note}</div>;
}

/**
 * O card de partidas de ônibus da conversa (`bus_offers`): a seleção curta do
 * intelligence-ui-kit (`BusOfferShortlist` — os mesmos cards da bancada) com as
 * viagens que o agente separou, e "Ver as N partidas", que abre a página da
 * bancada em diálogo (`BusResultsDialog`).
 *
 * Card VIVO (`onConfirmSeats`): `Selecionar` abre a escolha de poltrona por
 * trecho no próprio card (ou em tela cheia no celular) — também quando a viagem
 * vem da lista inteira, que fecha —, e o `Selecionar` dela devolve a viagem com
 * as poltronas. Sem ele (blocos antigos), o pick é o
 * quick-reply de texto (`onPickOffer`). Card congelado: tudo visível, nada a
 * escolher. A posição devolvida é a da lista COMPLETA — a ordem do `allOffers`.
 */
export function BusOffersList({
  offers,
  allOffers,
  leg,
  route,
  roundTrip = false,
  outboundPick,
  travelers = [{ id: TRAVELER_ID, name: '' }],
  autoExpand,
  note,
  day,
  dayBounds,
  onPickDay,
  onPickOffer,
  onConfirmSeats,
}: {
  readonly offers: ChatUiBusOffer[];
  /** Lista completa (até CHAT_UI_MAX_ALL_BUS_OFFERS) — "Ver as N partidas". */
  readonly allOffers?: ChatUiBusOffer[];
  /** Perna do ida-e-volta. Ausente = ida. */
  readonly leg?: 'outbound' | 'return';
  /** As cidades da perna como a pessoa pediu. */
  readonly route?: { from: string; to: string };
  /** Ida e volta: o stepper das duas pernas. */
  readonly roundTrip?: boolean;
  /** No card da volta: a ida já fechada. */
  readonly outboundPick?: BusOutboundPickView;
  /** Quem viaja — as iniciais e a chave de cada poltrona. Ausente ⇒ quem
   *  conversa, sem nome. */
  readonly travelers?: readonly BusSeatTraveler[];
  /** A viagem que abre já na escolha de poltrona. */
  readonly autoExpand?: string;
  readonly note?: string;
  /** O dia buscado da perna e os limites da faixa de dias (frame 04). */
  readonly day?: string;
  readonly dayBounds?: { min?: string; max?: string };
  /** Um dia da faixa: a perna buscada de novo nesse dia. Sem ele, sem faixa. */
  readonly onPickDay?: (day: string) => void;
  readonly onPickOffer?: PickAt;
  readonly onConfirmSeats?: ConfirmSeats;
}): ReactNode {
  const i18n = useGenUiI18n();
  const [listOpen, setListOpen] = useState(false);
  const [expanded, setExpanded] = useState(onConfirmSeats ? autoExpand : undefined);
  const all = allOffers ?? offers;
  const cities = legCitiesOf(all, route);
  const legName = leg ?? 'outbound';
  const interactive = Boolean(onConfirmSeats ?? onPickOffer);
  const projected = withHighlights(
    i18n.t,
    toBusFilterableOffers(all, cities, 1, busViewCopy(i18n, legName)),
    all,
  );
  const views = interactive ? projected : frozen(projected);

  const select = (offerId: string) => {
    // A planta abre SEMPRE no card da conversa: escolhida na lista inteira, a
    // lista fecha e a viagem aparece aberta no card.
    if (onConfirmSeats) {
      setListOpen(false);
      setExpanded(offerId);
      return;
    }
    if (onPickOffer) {
      setListOpen(false);
      locate(all, offerId, onPickOffer);
    }
  };
  const seats = onConfirmSeats
    ? seatExpansionOf({
        all,
        cities,
        travelers,
        scope: roundTrip ? legName : 'oneWay',
        expanded,
        close: () => {
          setExpanded(undefined);
        },
        onConfirmSeats,
      })
    : {};

  return (
    <div className={`genui2 grid gap-3 ${BLOCK_ELEVATION}`}>
      {note && <CardNote note={note} />}
      <BusOfferShortlist
        offers={shortlistOf(offers, views, expanded)}
        total={all.length}
        copy={busShortlistCopy(i18n.t)}
        steps={busLegSteps(i18n, {
          roundTrip,
          leg: legName,
          cities,
          outboundPick,
        })}
        {...seats}
        {...dayStripOf(i18n, { day, dayBounds, onPickDay })}
        onSeeAll={() => {
          setListOpen(true);
        }}
        onSelect={select}
      />
      {listOpen && (
        <BusResultsDialog
          offers={all}
          views={views}
          cities={cities}
          leg={legName}
          roundTrip={roundTrip}
          {...(interactive ? { onSelect: select } : {})}
          onClose={() => {
            setListOpen(false);
          }}
        />
      )}
    </div>
  );
}
